import React, { useState, useEffect, useCallback } from "react";
import { useLoading } from "../../context/LoadingContext";
import DataTable from "../Requirement/components/common/ReusableTable";
import SearchFilter from "../Requirement/components/common/SearchFilter";
import {
  fetchData,
  postQuery,
} from "../Requirement/services/RequirementServices";
import {
  FormControl,
  FormControlLabel,
  Checkbox,
  Typography,
  TextField,
  Button,
  Box,
} from "@mui/material";
import { Tooltip, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import dayjs from "dayjs";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { v4 as uuidv4 } from "uuid";
import AlertModal from "../Requirement/components/common/AlerModel";
import config from "../../aws-exports";
import "../../styles/requirement.css";
import "../../styles/general.css";
import "../../styles/buyer.css";
import FPMatchingOffers from "./MatchingOffers";
import RFQViewer from "./FinanceRfqOverview";

const AddOfferForm = ({
  onClose,
  rfqId,
  rfqNo,
  bankName,
  bankId,
  buyerEmail,
  mode = "add",
  offerData = null,
  onSuccess,
  onCancel,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const initialFormData = {
    BankName: bankName || "",
    BankID: bankId || "",
    Instrument: "",
    InterestRateMargin: "",
    IssuanceCharges: "",
    ConfirmationCharges: "",
    TenorOffered: "",
    Currency: "",
    TotalCost: "",
    ConditionSummary: "",
    OfferValidity: "",
    requiredDocuments: {
      commercialInvoice: false,
      billOfLading: false,
      certificateOfAnalysis: false,
      packingList: false,
      certificateOfOrigin: false,
      insurancePolicy: false,
      inspectionCertificate: false,
    },
  };

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (mode === "update" && offerData) {
      const requiredDocs = offerData.RequiredDocuments
        ? offerData.RequiredDocuments.split(", ").reduce((acc, doc) => {
          const keyMap = {
            "Commercial Invoice": "commercialInvoice",
            "Bill of Lading": "billOfLading",
            "Certificate of Analysis": "certificateOfAnalysis",
            "Packing List": "packingList",
            "Certificate of Origin": "certificateOfOrigin",
            "Insurance Policy": "insurancePolicy",
            "Inspection Certificate": "inspectionCertificate",
          };
          const key = keyMap[doc];
          if (key) acc[key] = true;
          return acc;
        }, { ...initialFormData.requiredDocuments })
        : { ...initialFormData.requiredDocuments };

      setFormData({
        BankName: offerData.BankName || bankName || "",
        BankID: offerData.BankID || bankId || "",
        Instrument: offerData.Instrument || "",
        InterestRateMargin: offerData.InterestRateMargin?.toString() || "",
        IssuanceCharges: offerData.IssuanceCharges?.toString() || "",
        ConfirmationCharges: offerData.ConfirmationCharges?.toString() || "",
        TenorOffered: offerData.TenorOffered || "",
        Currency: offerData.Currency || "",
        TotalCost: offerData.TotalCost?.toString() || "",
        ConditionSummary: offerData.ConditionSummary || "",
        OfferValidity: offerData.OfferValidity
          ? dayjs(offerData.OfferValidity).format("YYYY-MM-DD")
          : "",
        requiredDocuments: requiredDocs,
      });
    } else {
      setFormData({
        ...initialFormData,
        BankName: bankName || "",
        BankID: bankId || "",
      });
    }
  }, [mode, offerData, bankName, bankId]);

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        requiredDocuments: {
          ...prev.requiredDocuments,
          [name]: checked,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.BankName) newErrors.BankName = "Bank Name is required";
    else if (formData.BankName.length > 100)
      newErrors.BankName = "Bank Name must not exceed 100 characters";

    if (!formData.BankID) newErrors.BankID = "Bank ID is required";
    else if (!/^[a-zA-Z0-9]+$/.test(formData.BankID))
      newErrors.BankID = "Bank ID must be alphanumeric";
    else if (formData.BankID.length > 50)
      newErrors.BankID = "Bank ID must not exceed 50 characters";

    if (!formData.Instrument) newErrors.Instrument = "Instrument is required";
    else if (formData.Instrument.length > 100)
      newErrors.Instrument = "Instrument must not exceed 100 characters";

    if (!formData.InterestRateMargin)
      newErrors.InterestRateMargin = "Interest Rate Margin is required";
    else if (
      isNaN(formData.InterestRateMargin) ||
      Number(formData.InterestRateMargin) <= 0
    )
      newErrors.InterestRateMargin =
        "Interest Rate Margin must be a positive number";
    else if (!/^\d+(\.\d{1,2})?$/.test(formData.InterestRateMargin))
      newErrors.InterestRateMargin =
        "Interest Rate Margin must have up to 2 decimal places";

    if (!formData.IssuanceCharges)
      newErrors.IssuanceCharges = "Issuance Charges is required";
    else if (
      isNaN(formData.IssuanceCharges) ||
      Number(formData.IssuanceCharges) <= 0
    )
      newErrors.IssuanceCharges = "Issuance Charges must be a positive number";
    else if (!/^\d+(\.\d{1,2})?$/.test(formData.IssuanceCharges))
      newErrors.IssuanceCharges =
        "Issuance Charges must have up to 2 decimal places";

    if (!formData.ConfirmationCharges)
      newErrors.ConfirmationCharges = "Confirmation Charges is required";
    else if (
      isNaN(formData.ConfirmationCharges) ||
      Number(formData.ConfirmationCharges) <= 0
    )
      newErrors.ConfirmationCharges =
        "Confirmation Charges must be a positive number";
    else if (!/^\d+(\.\d{1,2})?$/.test(formData.ConfirmationCharges))
      newErrors.ConfirmationCharges =
        "Confirmation Charges must have up to 2 decimal places";

    if (!formData.TenorOffered)
      newErrors.TenorOffered = "Tenor Offered is required";
    else if (formData.TenorOffered.length > 50)
      newErrors.TenorOffered = "Tenor Offered must not exceed 50 characters";

    if (!formData.Currency) newErrors.Currency = "Currency is required";
    else if (!/^[A-Z]{3}$/.test(formData.Currency))
      newErrors.Currency = "Currency must be a 3-letter code (e.g., USD)";

    if (!formData.TotalCost) newErrors.TotalCost = "Total Cost is required";
    else if (isNaN(formData.TotalCost) || Number(formData.TotalCost) <= 0)
      newErrors.TotalCost = "Total Cost must be a positive number";
    else if (!/^\d+(\.\d{1,2})?$/.test(formData.TotalCost))
      newErrors.TotalCost = "Total Cost must have up to 2 decimal places";

    if (!formData.ConditionSummary)
      newErrors.ConditionSummary = "Condition Summary is required";
    else if (formData.ConditionSummary.length > 500)
      newErrors.ConditionSummary =
        "Condition Summary must not exceed 500 characters";

    if (!formData.OfferValidity)
      newErrors.OfferValidity = "Offer Validity is required";
    else if (!dayjs(formData.OfferValidity).isValid())
      newErrors.OfferValidity = "Offer Validity must be a valid date";
    else if (dayjs(formData.OfferValidity).isBefore(dayjs().startOf("day")))
      newErrors.OfferValidity = "Offer Validity must be a future date";

    if (!Object.values(formData.requiredDocuments).some((value) => value))
      newErrors.requiredDocuments = "At least one document must be selected";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getDocumentsString = (data) => {
    const labelMap = {
      commercialInvoice: "Commercial Invoice",
      billOfLading: "Bill of Lading",
      certificateOfAnalysis: "Certificate of Analysis",
      packingList: "Packing List",
      certificateOfOrigin: "Certificate of Origin",
      insurancePolicy: "Insurance Policy",
      inspectionCertificate: "Inspection Certificate",
    };
    return Object.entries(data)
      .filter(([_, value]) => value)
      .map(([key]) => labelMap[key])
      .join(", ");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!validateForm()) {
        throw new Error("Form validation failed");
      }

      const financeOffersInput = {
        FinanceRfqOfferID: mode === "update" ? offerData.FinanceRfqOfferID : uuidv4(),
        FinanceRfqID: rfqId,
        BankName: formData.BankName,
        BankID: formData.BankID,
        Instrument: formData.Instrument,
        ConfirmationCharges: parseFloat(formData.ConfirmationCharges) || 0,
        IssuanceCharges: parseFloat(formData.IssuanceCharges) || 0,
        InterestRateMargin: parseFloat(formData.InterestRateMargin) || 0,
        TenorOffered: formData.TenorOffered,
        Currency: formData.Currency,
        TotalCost: parseFloat(formData.TotalCost) || 0,
        ConditionSummary: formData.ConditionSummary,
        OfferValidity: formData.OfferValidity,
        RequiredDocuments: getDocumentsString(formData.requiredDocuments),
        Status: "pending",
      };

      const financeOfferBody = {
        target: mode === "update" ? "updateFinanceOffers" : "createFinanceOffers",
        financeOffersInput,
      };

      const financeResult = await postQuery(financeOfferBody);
      if (!financeResult?.items) {
        throw new Error("Failed to create/update RFQ bank offer");
      }

      const mailMessage = `
        <p>A new bank offer has been ${mode === "update" ? "updated" : "submitted"}. Please find all the submitted information below for your reference.</p>
        <strong><u>Bank Offer Details</u></strong>
        <p>
          <b>Bank Name: </b>${formData.BankName}<br/>
          <b>Bank ID: </b>${formData.BankID}<br/>
          <b>Instrument: </b>${formData.Instrument}<br/>
          <b>Interest Rate Margin (%): </b>${formData.InterestRateMargin}<br/>
          <b>Issuance Charges: </b>${formData.IssuanceCharges}<br/>
          <b>Confirmation Charges: </b>${formData.ConfirmationCharges}<br/>
          <b>Tenor Offered: </b>${formData.TenorOffered}<br/>
          <b>Currency: </b>${formData.Currency}<br/>
          <b>Total Cost: </b>${formData.TotalCost}<br/>
          <b>Condition Summary: </b><p>${formData.ConditionSummary}</p>
          <b>Offer Validity: </b>${formData.OfferValidity
          ? dayjs(formData.OfferValidity).format("DD-MM-YYYY")
          : ""
        }<br/>
          <b>Required Documents: </b>${getDocumentsString(
          formData.requiredDocuments
        )}<br/>
        </p>
        <p>Please review the bank offer details.</p>
      `;

      const bodyMail = {
        target: "financerfqmail",
        MailInput: {
          toAddresses: [
            "subhasish.mukherjee@agropreneurs.in",
            "tirtha.roy@agropreneurs.in",
            "amit.mitra@agropreneurs.in",
            buyerEmail,
          ],
          subject: `New Bank Offer ${mode === "update" ? "Update" : "Notification"}`,
          msg: mailMessage,
        },
      };

      await postQuery(bodyMail, "backendops");

      if (onSuccess) {
        onSuccess();
      }

      setFormData(initialFormData);
      setErrors({});

      setModal({
        show: true,
        type: "success",
        title: "Success!",
        message: `Bank offer ${mode === "update" ? "updated" : "submitted"} successfully.`,
        autoClose: false,
        nextAction: onClose,
      });
    } catch (error) {
      console.error("Submission error:", error.message);
      setErrors((prev) => ({
        ...prev,
        form: error.message || "An error occurred during submission",
      }));
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: error.message || `An error occurred during submission`,
        autoClose: false,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ width: "100%", p: 1 }}>
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />

      <div className="table-header">
        <div className="header-text">
          <div className="head-text">{mode === "update" ? `Update Offer: ${rfqNo}` : `Bank Offer: ${rfqNo}`}</div>
        </div>
        <div className="head-btn">
          <Button
            type="button"
            onClick={onCancel}
            className="submit-btn-more"
            sx={{
              color: "#fff",
              bgcolor: "#de6b14",
              "&:hover": { bgcolor: "#bbb" },
              textTransform: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="offer-form"
            disabled={isSubmitting}
            className="submit-btn-more"
            sx={{
              color: "#fff",
              bgcolor: "#de6b14",
              "&:hover": { bgcolor: "#fb8c00" },
              textTransform: "none",
              width: "auto",
            }}
          >
            {isSubmitting ? "Submitting..." : mode === "update" ? "Update" : "Submit"}
          </Button>
        </div>
      </div>

      <Box component="form" id="offer-form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <div className="row g-3">
          <div className="col-md-6">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Bank Name <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              name="BankName"
              value={formData.BankName}
              onChange={handleChange}
              placeholder="Enter Bank Name"
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              error={!!errors.BankName}
              helperText={errors.BankName}
              InputProps={{ readOnly: true }}
            />
          </div>
          <div className="col-md-6" style={{ display: "none" }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Bank ID <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              name="BankID"
              value={formData.BankID}
              onChange={handleChange}
              placeholder="Enter Bank ID"
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              error={!!errors.BankID}
              helperText={errors.BankID}
              InputProps={{ readOnly: true }}
              type="hidden"
            />
          </div>
          <div className="col-md-6">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Instrument <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              name="Instrument"
              value={formData.Instrument}
              onChange={handleChange}
              placeholder="Enter Instrument"
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              error={!!errors.Instrument}
              helperText={errors.Instrument}
            />
          </div>
          <div className="col-md-6">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Interest Rate Margin <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              type="number"
              name="InterestRateMargin"
              value={formData.InterestRateMargin}
              onChange={handleChange}
              placeholder="Enter Interest Rate Margin"
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              error={!!errors.InterestRateMargin}
              helperText={errors.InterestRateMargin}
              inputProps={{ step: "any" }}
            />
          </div>
          <div className="col-md-6">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Issuance Charges <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              type="number"
              name="IssuanceCharges"
              value={formData.IssuanceCharges}
              onChange={handleChange}
              placeholder="Enter Issuance Charges"
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              error={!!errors.IssuanceCharges}
              helperText={errors.IssuanceCharges}
              inputProps={{ step: "any" }}
            />
          </div>
          <div className="col-md-6">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Confirmation Charges <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              type="number"
              name="ConfirmationCharges"
              value={formData.ConfirmationCharges}
              onChange={handleChange}
              placeholder="Enter Confirmation Charges"
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              error={!!errors.ConfirmationCharges}
              helperText={errors.ConfirmationCharges}
              inputProps={{ step: "any" }}
            />
          </div>
          <div className="col-md-6">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Tenor Offered <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              name="TenorOffered"
              value={formData.TenorOffered}
              onChange={handleChange}
              placeholder="Enter Tenor Offered"
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              error={!!errors.TenorOffered}
              helperText={errors.TenorOffered}
            />
          </div>
          <div className="col-md-6">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Currency <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              name="Currency"
              value={formData.Currency}
              onChange={handleChange}
              placeholder="Enter Currency"
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              error={!!errors.Currency}
              helperText={errors.Currency}
            />
          </div>
          <div className="col-md-6">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Total Cost <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              type="number"
              name="TotalCost"
              value={formData.TotalCost}
              onChange={handleChange}
              placeholder="Enter Total Cost"
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              error={!!errors.TotalCost}
              helperText={errors.TotalCost}
              inputProps={{ step: "any" }}
            />
          </div>
          <div className="col-md-6">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Condition Summary <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              name="ConditionSummary"
              value={formData.ConditionSummary}
              onChange={handleChange}
              placeholder="Enter Condition Summary"
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              error={!!errors.ConditionSummary}
              helperText={errors.ConditionSummary}
            />
          </div>
          <div className="col-md-6">
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Offer Validity <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              type="date"
              name="OfferValidity"
              value={formData.OfferValidity}
              onChange={handleChange}
              placeholder="Enter Offer Validity"
              variant="outlined"
              size="small"
              sx={{ bgcolor: "white" }}
              error={!!errors.OfferValidity}
              helperText={errors.OfferValidity}
              inputProps={{
                min: dayjs().format("YYYY-MM-DD"),
              }}
            />
          </div>
          <div className="col-md-12">
            <FormControl fullWidth>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Required Documents <span style={{ color: "red" }}>*</span>
              </Typography>
              <div className="d-flex flex-wrap gap-3">
                {[
                  {
                    label: "Commercial Invoice",
                    name: "commercialInvoice",
                  },
                  { label: "Bill of Lading (BL)", name: "billOfLading" },
                  {
                    label: "Certificate of Analysis (COA)",
                    name: "certificateOfAnalysis",
                  },
                  { label: "Packing List", name: "packingList" },
                  {
                    label: "Certificate of Origin",
                    name: "certificateOfOrigin",
                  },
                  { label: "Insurance Policy", name: "insurancePolicy" },
                  {
                    label: "Inspection Certificate",
                    name: "inspectionCertificate",
                  },
                ].map((doc) => (
                  <FormControlLabel
                    key={doc.name}
                    control={
                      <Checkbox
                        name={doc.name}
                        checked={formData.requiredDocuments[doc.name]}
                        onChange={handleChange}
                        size="small"
                        sx={{
                          color: "#de6b14",
                          "&.Mui-checked": {
                            color: "#de6b14",
                          },
                        }}
                      />
                    }
                    label={doc.label}
                  />
                ))}
              </div>
              {errors.requiredDocuments && (
                <Typography variant="caption" color="error">
                  {errors.requiredDocuments}
                </Typography>
              )}
            </FormControl>
          </div>

          {errors.form && (
            <Typography variant="caption" color="error" sx={{ mt: 2 }}>
              {errors.form}
            </Typography>
          )}
        </div>
      </Box>
    </Box>
  );
};

const FPFinanceRfq = () => {
  const navigate = useNavigate();
  const { setLoading, loading } = useLoading();
  const [rfqList, setRfqList] = useState([]);
  const [rowStart, setRowStart] = useState(0);
  const [pageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [activeUser, setActiveUser] = useState("");
  const [error, setError] = useState(null);
  const [INRToUsd] = useState(0.012);
  const [searchValues, setSearchValues] = useState({
    Title: "",
    ApplicantName: "",
    TypeOfLc: "",
    searchPriceRange: "",
  });
  const [userInfo, setUserInfo] = useState([]);
  const [selectedRfq, setSelectedRfq] = useState(null);
  const [bankList, setBankList] = useState([]);
  const [isBankListLoaded, setIsBankListLoaded] = useState(false);
  const [offerMode, setOfferMode] = useState("view");
  const [selectedOfferData, setSelectedOfferData] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showAddOfferForm, setShowAddOfferForm] = useState(false);

  const priceOptions = [
    { value: "", label: "Select Range" },
    { value: "0-1000", label: "0-1000 USD" },
    { value: "1000-2000", label: "1000-2000 USD" },
    { value: "2000-5000", label: "2000-5000 USD" },
    { value: "5000-5000000", label: "Above 5000 USD" },
  ];
  const loadBankList = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listBankMaster",
          userOrder: "BankName",
          rowStart: 0,
          pageSize: 1,
        }),
      ]);
      const totalCount = initialResponse?.total || initialResponse?.length || 0;
      if (totalCount > 0) {
        const [allBanks] = await Promise.all([
          fetchData({
            target: "listBankMaster",
            userOrder: "BankName",
            rowStart: 0,
            pageSize: totalCount,
          }),
        ]);
        setBankList(allBanks.items || allBanks);
        setIsBankListLoaded(true);
      } else {
        setBankList([]);
        setIsBankListLoaded(true);
      }
    } catch (error) {
      console.error("Error loading bank list: ", error);
      setBankList([]);
      setIsBankListLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const getBankNameById = useCallback(
    (bankId) => {
      if (!bankId || !isBankListLoaded) return "";
      const parsedBankId = parseInt(bankId, 10);
      const bank = bankList.find(
        (b) => b.BankID === parsedBankId || b.id === parsedBankId
      );
      return bank?.BankName || "";
    },
    [bankList, isBankListLoaded]
  );

  const searchRFQs = useCallback(
    async (searchValues, newRowStart = rowStart) => {
      setLoading(true);
      try {
        const filterTerms = {
          BankID: parseInt(userInfo[0]?.OrgId, 10) || 0,
        };

        if (searchValues.Title) {
          filterTerms.TitlePartial = searchValues.Title;
        }
        if (searchValues.ApplicantName) {
          filterTerms.ApplicantNamePartial = searchValues.ApplicantName;
        }
        if (searchValues.TypeOfLc) {
          filterTerms.TypeOfLcPartial = searchValues.TypeOfLc;
        }
        if (searchValues.TradeValue) {
          const [min, max] = searchValues.TradeValue .split("-").map((val) => parseInt(val, 10));
          if (searchValues.TradeValue === "5000-5000000") {
            filterTerms.TradeValueGTE = min; // Greater than or equal to 5000
           
            // No upper bound for "Above 5000 USD"
          } else {
            filterTerms.TradeValueGTE = min; // Greater than or equal to min
            filterTerms.TradeValueLTE = max; // Less than or equal to max
          }
        }
        // if (filterTerms.searchPriceRange) {
        //   const [min, max] = filterTerms.searchPriceRange.split("-");
        //   if (min === "Above") {
        //     filterTerms.TradeValueGTE = max;
        //   } else {
        //     filterTerms.TradeValueGTE = min;
        //     filterTerms.TradeValueLTE = max;
        //   }
        //   delete filterTerms.searchPriceRange;
        // }
       
        const body = {
          target: "listFinanceRfqsBank",
          userOrder: "CreatedAt",
          rowStart: newRowStart,
          pageSize: pageSize,
          filterTerms,
        };

        const response = await fetchData(body);
        const modifiedItems = (response.items || response).map((item) => ({
          ...item,
          id: item.Id,
        }));
        setRfqList(modifiedItems);
        setTotalItems(response.total || 0);
      } catch (error) {
        console.error("Error loading RFQs: ", error);
        setRfqList([]);
        setTotalItems(0);
        setError("Failed to load RFQs. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [pageSize, rowStart, setLoading, userInfo]
  );

  const fields = [
    {
      name: "Title",
      label: "Title",
      type: "text",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, Title: value })),
      value: searchValues.Title,
    },
    {
      name: "ApplicantName",
      label: "Applicant Name",
      type: "text",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, ApplicantName: value })),
      value: searchValues.ApplicantName,
    },
    {
      name: "TypeOfLc",
      label: "Type Of LC",
      type: "text",
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, TypeOfLc: value })),
      value: searchValues.TypeOfLc,
    },
    {
      name: "TradeValue",
      label: "Trade Value",
      type: "dropdown",
      options: priceOptions,
      value: searchValues.TradeValue,
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, TradeValue: value })),
    },
  ];

  const handlePageChange = useCallback(
    (page) => {
      const newRowStart = (page - 1) * pageSize;
      setRowStart(newRowStart);
      searchRFQs(searchValues, newRowStart);
    },
    [pageSize, searchValues, searchRFQs]
  );

  const handleSearch = () => {
    setRowStart(0);
    searchRFQs(searchValues, 0);
  };

  const getUserInfo = useCallback(async (userEmail) => {
    try {
      const response = await fetchData({
        target: "listAMTPLUsers",
        userOrder: "AMTPLUserID",
        rowStart: 0,
        pageSize: 1,
        filterTerms: { EmailID: userEmail },
      });
      setUserInfo(response.items || []);
    } catch (error) {
      console.error("Error loading buyer info:", error);
      setUserInfo([]);
    }
  }, []);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_FINANCE_PARTNERS")) {
          await Auth.signOut();
          navigate("/login/finance-partner");
        } else {
          setActiveUser(user.attributes.email);
          getUserInfo(user.attributes.email);
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await Auth.signOut();
        navigate("/login/finance-partner");
      }
    };
    checkUser();
  }, [navigate, getUserInfo]);

  useEffect(() => {
    if (activeUser) {
      loadBankList();
    }
  }, [activeUser, loadBankList]);

  useEffect(() => {
    if (
      activeUser &&
      userInfo.length > 0 &&
      userInfo[0]?.OrgId &&
      isBankListLoaded
    ) {
      searchRFQs(searchValues, rowStart);
    }
  }, [
    rowStart,
    pageSize,
    activeUser,
    userInfo,
    searchRFQs,
    searchValues,
    isBankListLoaded,
  ]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString;
    }
  };

  const convertToINR = useCallback((usdValue) => {
    if (!usdValue || usdValue === "") return "0.00";
    const inrValue = parseFloat(usdValue) / INRToUsd;
    return isNaN(inrValue) ? "0.00" : inrValue.toFixed(2);
  }, [INRToUsd]);

  const columns = [
    { id: "RfqId", numeric: false, label: "RFQ ID" },
    { id: "Title", numeric: false, label: "Title" },
    { id: "ApplicantName", numeric: false, label: "Applicant Name" },
    { id: "BeneficiaryName", numeric: false, label: "Beneficiary Name" },
    { id: "TypeOfLc", numeric: false, label: "Type Of LC" },
    { id: "TradeValue", numeric: false, label: "Trade Value" },
    { id: "LcAmount", numeric: false, label: "LC Amount" },
  ];

  const handleOpenAddOffer = ({ offer, mode } = { mode: "add" }) => {
    if (!selectedRfq) {
      alert("Please select an RFQ from the table to add an offer.");
      return;
    }
    if (!isBankListLoaded) {
      alert("Bank list is still loading. Please try again in a moment.");
      return;
    }
    if (!userInfo[0]?.OrgId) {
      alert("User organization ID is not available. Please try again.");
      return;
    }
    const bankName = getBankNameById(userInfo[0]?.OrgId);
    if (!bankName) {
      alert(
        "Unable to retrieve bank name for the provided Bank ID. Please ensure the Bank ID is valid."
      );
      return;
    }
    setOfferMode(mode);
    setSelectedOfferData(offer || null);
    setShowAddOfferForm(true);
  };

  const handleRowClick = (row) => {
    setSelectedRfq(row);
    setOfferMode("view");
    setSelectedOfferData(null);
    setShowAddOfferForm(false); // Ensure form is closed on row click
  };

  const handleActionEditClick = (event, offer, mode) => {
    event.stopPropagation(); // Prevent row click from triggering
    setOfferMode("update");
    setSelectedOfferData(offer);
    setShowAddOfferForm(true);
  };

  const handleCloseAddOffer = () => {
    setShowAddOfferForm(false);
    setOfferMode("view");
    setSelectedOfferData(null);
  };

  const handleCancelAddOffer = () => {
    setShowAddOfferForm(false);
    setOfferMode("view");
    setSelectedOfferData(null);
  };

  return (
    <div className="buyer-layout card">
      <div className="container-fluid">
        <div className="table-header-re">
          <div className="head-btn"></div>
        </div>
        <div className="search-filter4">
          <div className="title4">
            <div className="head-text">RFQ</div>
          </div>
          <div className="filter4">
            <SearchFilter fields={fields} onSearch={handleSearch} />
          </div>
        </div>
        <div className="table-content">
          {error && (
            <Typography variant="caption" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          <DataTable
            columns={columns}
            data={rfqList}
            total={totalItems}
            title="RFQs"
            rowsPerPage={pageSize}
            onPageChange={handlePageChange}
            currentPage={Math.floor(rowStart / pageSize) + 1}
            onRowClick={handleRowClick}
            selectedRows={selectedRfq ? [selectedRfq.Id] : []}
          />
          {selectedRfq && (
            <Box sx={{ mt: 3 }} className="card">
              {userInfo.length > 0 && userInfo[0]?.OrgId ? (
                <FPMatchingOffers
                  key={`${selectedRfq.Id}-${refreshTrigger}`}
                  activeUser={activeUser}
                  userInfo={userInfo}
                  handleOpenAddOffer={handleOpenAddOffer}
                  loading={loading}
                  isBankListLoaded={isBankListLoaded}
                  selectedRfq={selectedRfq}
                  handleCancelAddOffer={handleCancelAddOffer}
                  handleActionEditClick={handleActionEditClick}
                />
              ) : (
                <Typography>Loading...</Typography>
              )}
              {showAddOfferForm && userInfo.length > 0 && userInfo[0]?.OrgId && (
                <div className="card mb-2">
                  <AddOfferForm
                    onClose={handleCloseAddOffer}
                    rfqId={selectedRfq?.Id || ""}
                    rfqNo={selectedRfq?.RfqId || ""}
                    bankName={getBankNameById(userInfo[0]?.OrgId) || ""}
                    bankId={userInfo[0]?.OrgId || ""}
                    buyerEmail={activeUser || ""}
                    mode={offerMode}
                    offerData={selectedOfferData}
                    onSuccess={() => {
                      setRefreshTrigger((prev) => prev + 1);
                      handleCloseAddOffer();
                    }}
                    onCancel={handleCloseAddOffer}
                  />
                </div>
              )}
              <RFQViewer key={selectedRfq.Id} rfqData={selectedRfq} />
            </Box>
          )}
        </div>
      </div>
    </div>
  );
};

export default FPFinanceRfq;